import {
  applyCanvasMutation,
  type CanvasMutationHandler,
} from "../canvas/mutations.js";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type PointerEvent,
  type ReactElement,
} from "react";
import {
  WidgetFieldEditSessionProvider,
  type WidgetFieldEditSession,
} from "./field-edit-session.js";
import * as Y from "yjs";
import { GripVertical, Lock, Plus, Trash2 } from "lucide-react";
import type { WidgetKind } from "@playbookai/types";
import {
  WIDGET_CURRENT_SCHEMA_VERSIONS,
  WIDGET_PARAMS_SCHEMAS,
} from "@playbookai/widget-schemas";
import type { CanvasWidgetLockState } from "../canvas/types.js";
import { cn } from "../lib/utils.js";
import { SELECTED } from "../lib/surfaces.js";
import { Button } from "../atoms/index.js";
import { Tooltip } from "../molecules/index.js";
import { materializeYjsParams, reconcileYjsParams } from "./yjs-params.js";
import { getWidget } from "./registry.js";
import {
  MAX_DIAGNOSTIC_KIND_LENGTH,
  sanitizeKindForDisplay,
  UnsupportedWidget,
} from "./UnsupportedWidget.js";
import { ModuleCard, type ModuleCardAccent } from "./ModuleCard.js";
import { WidgetErrorBoundary } from "../shell/entity-dashboard/WidgetErrorBoundary.js";
import { CanvasWidgetRemoveDialog } from "./CanvasWidgetRemoveDialog.js";
import { ExpandedCardModal } from "./ExpandedCardModal.js";
import { ExpandedWidgetEditor, hasExpandedWidget } from "./expanded-editors.js";
import type {
  SqlWidgetDef,
  WidgetAskAIRequest,
  WidgetChangeOptions,
  WidgetDef,
  WidgetHostMetadata,
  YjsWidgetDef,
} from "./types.js";
import {
  useWidgetEntityResolver,
  type WidgetEntityHandle,
} from "./entity-resolver.js";

const AFFORDANCE_TONE: Record<ModuleCardAccent, string> = {
  cyan: "bg-pb-cyan/10 text-pb-cyan-text border-pb-cyan/30",
  amber: "bg-pb-amber/10 text-pb-amber-text border-pb-amber/30",
  emerald: "bg-pb-teal/10 text-pb-teal-text border-pb-teal/30",
  purple: "bg-pb-purple/10 text-pb-purple-text border-pb-purple/30",
  rose: "bg-pb-red/10 text-pb-red-text border-pb-red/30",
  neutral: "bg-pb-card-elevated text-pb-text-muted border-pb-border",
};

/* The widget's accent as a small swatch on the neutral action button: the
   same colours as ModuleCard's header accent dot. */
const AFFORDANCE_SWATCH: Record<ModuleCardAccent, string> = {
  cyan: "bg-pb-cyan",
  amber: "bg-pb-amber",
  emerald: "bg-pb-teal",
  purple: "bg-pb-purple",
  rose: "bg-pb-red",
  neutral: "bg-pb-text-muted",
};

/** Widget-specific header action routed into the host edit path. */
function HeaderAffordancePill({
  label,
  accent,
  onClick,
  pressed,
}: {
  label: string;
  accent: ModuleCardAccent;
  onClick?: (() => void) | undefined;
  pressed?: boolean | undefined;
}): ReactElement {
  if (onClick) {
    return (
      <Button
        size="xs"
        pressed={pressed}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <span
          aria-hidden
          className={cn(
            "h-2 w-2 shrink-0 rounded-full",
            AFFORDANCE_SWATCH[accent],
          )}
        />
        <Plus className="w-3 h-3" aria-hidden />
        {label}
      </Button>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-dense-1 font-medium border ${AFFORDANCE_TONE[accent]} ${pressed ? "ring-1 ring-current/30" : ""}`}
    >
      <Plus className="w-3 h-3" aria-hidden />
      {label}
    </span>
  );
}

export interface CanvasWidgetProps {
  readonly onCanvasMutation?: CanvasMutationHandler | undefined;
  readonly widgetId: string;
  readonly ymap: Y.Map<unknown>;
  readonly doc: Y.Doc;
  readonly readOnly?: boolean | undefined;
  readonly lockState?: CanvasWidgetLockState | null | undefined;
  readonly onSelect?: ((widgetId: string) => void) | undefined;
  readonly selected?: boolean | undefined;
  readonly onDragStart?: ((widgetId: string) => void) | undefined;
  readonly onDragMove?:
    | ((widgetId: string, dx: number, dy: number) => void)
    | undefined;
  readonly onDragEnd?: ((widgetId: string) => void) | undefined;
  readonly onEditingChange?:
    | ((widgetId: string, editing: boolean) => void)
    | undefined;
  readonly onRemove?: ((widgetId: string) => void) | undefined;
  readonly onAskAI?: ((request: WidgetAskAIRequest) => void) | undefined;
  readonly solveId?: string | undefined;
  /**
   * @deprecated SQL widgets now always use the integrated, single-header
   * layout. Retained temporarily so downstream TypeScript consumers can
   * migrate without a breaking release; the value is ignored.
   */
  readonly widgetHeaderLayout?: "separate" | "integrated" | undefined;
  readonly hostMetadata?: WidgetHostMetadata | undefined;
}

/**
 * Canvas widget adapter. Dispatches by `def.storage`:
 *   - `'sql'` — renders `<ModuleCard>` with the def's accent/icon/title/
 *     subtitle and the Component as the body slot. Hosts can place the
 *     drag grip inside that header instead of a separate labelled strip.
 *   - `'yjs'` — legacy sticky path (kept for back-compat; will be removed
 *     once `solve-feature/canvas/notes.ts` lands).
 */
export function CanvasWidget(props: CanvasWidgetProps): ReactElement | null {
  const {
    ymap,
    widgetId,
    readOnly,
    onSelect,
    selected,
    onDragStart,
    onDragMove,
    onDragEnd,
    onRemove,
  } = props;

  const readOnlyRef = useRef(Boolean(readOnly));
  const dragTeardownRef = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    readOnlyRef.current = Boolean(readOnly);
    if (readOnly) dragTeardownRef.current?.();
  }, [readOnly]);
  useEffect(() => (): void => dragTeardownRef.current?.(), []);

  const [, forceRerender] = useState(0);
  useEffect(() => {
    const observe = (): void => forceRerender((n) => n + 1);
    ymap.observe(observe);
    return (): void => ymap.unobserve(observe);
  }, [ymap]);

  // `ymap.get` returns whatever a client wrote, so the value is narrowed at
  // runtime rather than asserted: a non-string `kind` reaching the fallback used
  // to throw out of THIS render, which is not wrapped by the per-widget
  // `WidgetErrorBoundary` (that sits inside the body components) and so would
  // unmount the whole shared WidgetLayer.
  const rawKind = ymap.get("kind");
  if (rawKind === undefined || rawKind === null || rawKind === "") return null;
  if (typeof rawKind !== "string") {
    // Corrupt rather than merely half-written — surface it instead of hiding it,
    // and describe the value by type: the value itself is not renderable.
    console.warn(
      `@playbookai/ui: non-string widget kind of type '${typeof rawKind}' (widgetId=${widgetId})`,
    );
    return <UnsupportedWidget kind={`<${typeof rawKind}>`} />;
  }
  const kind = rawKind as WidgetKind;
  const def = getWidget(kind);
  if (!def) {
    // Bound ONCE, before either use. `kind` is untrusted doc content that syncs
    // to every peer, and this render re-runs on each `ymap` change, so neither
    // the console nor the DOM should ever see the full value.
    const diagnosticKind = sanitizeKindForDisplay(
      kind,
      MAX_DIAGNOSTIC_KIND_LENGTH,
    );
    console.warn(
      `@playbookai/ui: unknown widget kind '${diagnosticKind}' (widgetId=${widgetId})`,
    );
    return <UnsupportedWidget kind={diagnosticKind} />;
  }

  const isDefaultRoster = ymap.get("isDefaultRoster") === true;
  const drag: DragProps = {
    label: def.label,
    readOnly,
    readOnlyRef,
    dragTeardownRef,
    selected,
    widgetId,
    onSelect,
    onDragStart,
    onDragMove,
    onDragEnd,
    onRemove: isDefaultRoster ? undefined : onRemove,
  };

  if (def.storage === "yjs") {
    return <YjsWidgetBody {...props} def={def} kind={kind} drag={drag} />;
  }
  return <SqlWidgetBody {...props} def={def} kind={kind} drag={drag} />;
}

// ---------------------------------------------------------------------------
// Drag overlay shared by both paths
// ---------------------------------------------------------------------------

interface DragProps {
  readonly label: string;
  readonly readOnly?: boolean | undefined;
  readonly readOnlyRef: MutableRefObject<boolean>;
  readonly dragTeardownRef: MutableRefObject<(() => void) | null>;
  readonly selected?: boolean | undefined;
  readonly widgetId: string;
  readonly onSelect?: ((widgetId: string) => void) | undefined;
  readonly onDragStart?: ((widgetId: string) => void) | undefined;
  readonly onDragMove?:
    | ((widgetId: string, dx: number, dy: number) => void)
    | undefined;
  readonly onDragEnd?: ((widgetId: string) => void) | undefined;
  readonly onRemove?: ((widgetId: string) => void) | undefined;
}

function makeDragPointerDown<T extends HTMLElement>(
  drag: DragProps,
): (e: PointerEvent<T>) => void {
  return (e: PointerEvent<T>): void => {
    drag.dragTeardownRef.current?.();
    if (drag.readOnlyRef.current) return;
    drag.onSelect?.(drag.widgetId);
    if (!drag.onDragMove) return;
    e.preventDefault();
    e.stopPropagation();
    const activePointerId = e.pointerId;
    let last = { x: e.clientX, y: e.clientY };
    let dragStarted = false;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    let cleaned = false;
    const cleanup = (): void => {
      if (cleaned) return;
      cleaned = true;
      if (dragStarted) drag.onDragEnd?.(drag.widgetId);
      try {
        if (target.hasPointerCapture(activePointerId)) {
          target.releasePointerCapture(activePointerId);
        }
      } catch {
        /* pointer already released */
      }
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      if (drag.dragTeardownRef.current === cleanup) {
        drag.dragTeardownRef.current = null;
      }
    };
    const move = (ev: globalThis.PointerEvent): void => {
      if (ev.pointerId !== activePointerId) return;
      if (drag.readOnlyRef.current) {
        cleanup();
        return;
      }
      const dx = ev.clientX - last.x;
      const dy = ev.clientY - last.y;
      last = { x: ev.clientX, y: ev.clientY };
      if (dx === 0 && dy === 0) return;
      if (!dragStarted) {
        dragStarted = true;
        drag.onDragStart?.(drag.widgetId);
      }
      drag.onDragMove!(drag.widgetId, dx, dy);
    };
    // Clean up on pointerup AND interrupted drags (pointercancel / lost
    // capture) so listeners never leak and onDragEnd fires once. Bound to the
    // initiating pointer so a second touch can't end an in-progress drag.
    const up = (ev: globalThis.PointerEvent): void => {
      if (ev.pointerId === activePointerId) cleanup();
    };
    const cancel = up;
    drag.dragTeardownRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  };
}

/** The grip is the only drag target; sibling actions and body inputs stay interactive. */
function HeaderDragGrip({ drag }: { drag: DragProps }): ReactElement {
  const canMove = !drag.readOnly && drag.onDragMove !== undefined;

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (drag.readOnly) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      drag.onSelect?.(drag.widgetId);
      return;
    }
    if (!canMove) return;

    const distance = event.shiftKey ? 1 : 24;
    const delta =
      event.key === "ArrowLeft"
        ? { dx: -distance, dy: 0 }
        : event.key === "ArrowRight"
          ? { dx: distance, dy: 0 }
          : event.key === "ArrowUp"
            ? { dx: 0, dy: -distance }
            : event.key === "ArrowDown"
              ? { dx: 0, dy: distance }
              : null;
    if (!delta) return;

    event.preventDefault();
    event.stopPropagation();
    drag.onSelect?.(drag.widgetId);
    drag.onDragStart?.(drag.widgetId);
    drag.onDragMove?.(drag.widgetId, delta.dx, delta.dy);
    drag.onDragEnd?.(drag.widgetId);
  };

  return (
    <Tooltip
      content={
        canMove
          ? "Hold and drag, or use arrow keys, to move"
          : "Moving unavailable; press Enter to select"
      }
      followAnchor
      wrapperClassName="shrink-0"
    >
      {/* eslint-disable-next-line ds/no-hand-built -- the canvas drag handle: pointer capture, arrow-key nudges and an item role description, which no design-system Button carries. */}
      <button
        type="button"
        onPointerDown={drag.readOnly ? undefined : makeDragPointerDown(drag)}
        onKeyDown={handleKeyDown}
        aria-label={
          canMove
            ? `${drag.label} — drag to move`
            : drag.readOnly
              ? `${drag.label} — moving unavailable`
              : `${drag.label} — select; moving unavailable`
        }
        aria-keyshortcuts={
          canMove ? "ArrowUp ArrowDown ArrowLeft ArrowRight" : undefined
        }
        aria-roledescription={canMove ? "movable widget" : undefined}
        className={cn(
          "flex h-6 w-6 shrink-0 touch-none select-none items-center justify-center rounded text-pb-text-muted",
          !canMove
            ? "cursor-default opacity-50"
            : "cursor-grab hover:pb-overlay-paint active:cursor-grabbing",
        )}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Yjs storage path — Hoshin canvas content cards (PB-234)
// ---------------------------------------------------------------------------

type YjsAccent = NonNullable<YjsWidgetDef<unknown>["accent"]>;

/** Header-label tint per accent. `neutral` is a plain dark label (not muted). */
const YJS_LABEL_TINT: Record<YjsAccent, string> = {
  neutral: "text-pb-text",
  cyan: "text-pb-cyan-text",
  amber: "text-pb-amber-text",
  emerald: "text-pb-teal-text",
  purple: "text-pb-purple-text",
  rose: "text-pb-red-text",
};

function YjsWidgetBody({
  onCanvasMutation,
  ymap,
  doc,
  def,
  kind,
  drag,
  lockState,
}: CanvasWidgetProps & {
  readonly def: YjsWidgetDef<unknown>;
  readonly kind: WidgetKind;
  readonly drag: DragProps;
}): ReactElement {
  const [, forceRerender] = useState(0);
  const rawParams = ymap.get("params");
  const bodyRef = useRef<HTMLDivElement>(null);
  const composingRef = useRef(false);
  const selectionRef = useRef<{
    element: HTMLInputElement | HTMLTextAreaElement;
    text: Y.Text;
    start: Y.RelativePosition;
    end: Y.RelativePosition;
    direction: "forward" | "backward" | "none";
  } | null>(null);

  // The leaf declares only its field name. Storage and cursor anchors stay
  // in the Yjs adapter, so the data-blind editor keeps its existing contract.
  const captureSelection = (): void => {
    const element = document.activeElement;
    if (
      !(
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement
      ) ||
      !bodyRef.current?.contains(element) ||
      element.disabled
    )
      return;
    const field = element.dataset.widgetTextField;
    const params = ymap.get("params");
    if (
      !field ||
      !def.textFields?.includes(field) ||
      !(params instanceof Y.Map)
    )
      return;
    const text = params.get(field);
    if (
      !(text instanceof Y.Text) ||
      element.selectionStart === null ||
      element.selectionEnd === null
    )
      return;
    const start = Math.min(element.selectionStart, text.length);
    const end = Math.min(element.selectionEnd, text.length);
    selectionRef.current = {
      element,
      text,
      start: Y.createRelativePositionFromTypeIndex(
        text,
        start,
        start === text.length ? -1 : 0,
      ),
      end: Y.createRelativePositionFromTypeIndex(
        text,
        end,
        end === start ? (start === text.length ? -1 : 0) : -1,
      ),
      direction: element.selectionDirection ?? "none",
    };
  };
  useLayoutEffect(() => {
    const selection = selectionRef.current;
    if (
      !selection ||
      composingRef.current ||
      selection.element.disabled ||
      document.activeElement !== selection.element
    )
      return;
    const start = Y.createAbsolutePositionFromRelativePosition(
      selection.start,
      doc,
    );
    const end = Y.createAbsolutePositionFromRelativePosition(
      selection.end,
      doc,
    );
    if (start?.type === selection.text && end?.type === selection.text) {
      selection.element.setSelectionRange(
        start.index,
        end.index,
        selection.direction,
      );
    }
  });

  useEffect(() => {
    // Char-merge params (PB-418) are a `Y.Map` whose free-text fields are nested
    // `Y.Text` — a shallow `observe` never sees a character insert into a child,
    // so subscribe with `observeDeep`. This is the SOLE local re-render path once
    // the `params` key stops being replaced per keystroke (the outer
    // `ymap.observe` above only fires when the key itself is set/replaced). The
    // handler does NOT filter its own write origin: this body keeps no optimistic
    // cache, so it must re-project from Yjs on every change, local or remote.
    if (rawParams instanceof Y.Map) {
      const observe = (): void => forceRerender((n) => n + 1);
      rawParams.observeDeep(observe);
      return (): void => rawParams.unobserveDeep(observe);
    }
    return undefined;
  }, [rawParams]);

  const data = def.paramsFromY(rawParams);
  const handleChange = (next: unknown): void => {
    // Never write params the kind's schema rejects: an over-limit value would
    // round-trip through the defensive `paramsFromY` back to the widget's blank
    // defaults (losing valid sibling fields) while leaving malformed state in
    // the shared Y.Doc. Editors clamp at the input; this is the last-line guard.
    if (
      drag.readOnlyRef.current ||
      !WIDGET_PARAMS_SCHEMAS[kind].safeParse(next).success
    )
      return;
    const mutation = {
      target: "widget" as const,
      id: drag.widgetId,
      entry: ymap,
    };
    if (onCanvasMutation && def.textFields?.length) {
      if (
        !applyCanvasMutation(
          onCanvasMutation,
          { ...mutation, action: "materialize" },
          () => {
            materializeYjsParams(
              doc,
              ymap,
              def.textFields!,
              def.paramsFromY(ymap.get("params")) as Record<string, unknown>,
            );
          },
        )
      )
        return;
    }
    const before = data as Record<string, unknown>;
    const after = next as Record<string, unknown>;
    const changed = [
      ...new Set([...Object.keys(before), ...Object.keys(after)]),
    ].filter(
      (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
    );
    // Coalesce single-field typing only. Discrete switches and compound saves
    // remain separate commands even when clicked within the typing interval.
    const field =
      changed.length === 1 &&
      ["string", "number"].includes(typeof after[changed[0]!])
        ? changed[0]
        : undefined;
    applyCanvasMutation(
      onCanvasMutation,
      { ...mutation, action: "edit", field },
      () => {
        if (def.textFields && def.textFields.length > 0) {
          // Reconcile in place so free-text fields get char-level `Y.Text` deltas
          // (concurrent edits merge instead of last-writer-wins clobber). `data` is
          // the projection THIS render showed the user — pass it as the baseline so
          // reconcile writes only the fields the user actually changed and never
          // reverts a concurrently-received remote edit to an untouched field (R10).
          reconcileYjsParams(
            doc,
            ymap,
            next as Record<string, unknown>,
            def.textFields,
            data as Record<string, unknown>,
          );
          return;
        }
        // Legacy whole-object path (e.g. the `image` card): replace the params value.
        doc.transact(() => {
          ymap.set("params", def.paramsToY(next, doc));
        });
      },
    );
    captureSelection();
  };

  const Component = def.Component as React.ComponentType<{
    data: unknown;
    onChange: (next: unknown) => void;
    readOnly?: boolean | undefined;
  }>;

  const accent: YjsAccent = def.accent ?? "neutral";

  return (
    <div
      className={cn(
        "group/widget relative flex h-full w-full flex-col overflow-hidden rounded-lg border bg-pb-card text-pb-text shadow-sm transition-[box-shadow,border-color,opacity] duration-motion-state ease-motion-standard motion-reduce:transition-none",
        // Selection is one treatment across the canvas (ADR-0092): the teal
        // glow, never a per-accent ring.
        drag.selected
          ? SELECTED
          : "border-pb-border hover:border-pb-border-strong",
        lockState?.dimmed === true ? "opacity-75 saturate-75" : "",
      )}
    >
      {/* Drag-handle header (the mockup's grip + title bar) — the whole strip is
          the drag surface, so it doubles as select-on-pointerdown. Kept
          hand-built (design-system adoption): a canvas drag handle; its
          `title` is a plain-text hint on a strip that takes no focus. */}
      <div
        onPointerDown={drag.readOnly ? undefined : makeDragPointerDown(drag)}
        title={drag.readOnly ? def.label : "Drag to move"}
        aria-label={drag.readOnly ? def.label : `${def.label} — drag to move`}
        className={cn(
          "flex h-7 shrink-0 select-none touch-none items-center gap-1.5 border-b border-pb-border bg-pb-card-elevated/50 px-2",
          drag.readOnly
            ? "cursor-default"
            : "cursor-grab active:cursor-grabbing",
        )}
      >
        <GripVertical
          className="h-3.5 w-3.5 shrink-0 text-pb-text-muted/70"
          aria-hidden
        />
        <span
          className={cn(
            "truncate text-dense-1 font-semibold",
            YJS_LABEL_TINT[accent],
          )}
        >
          {def.label}
        </span>
        {lockState ? (
          <span className="ms-1">
            <WidgetLockPill label={lockState.label} />
          </span>
        ) : null}
        {/* stopPropagation so pressing remove doesn't start a header drag —
            makeDragPointerDown captures the pointer + preventDefaults, which
            would otherwise swallow this button's click (mirrors the pattern
            HoshinXMatrixWidget uses for its header buttons). */}
        <span
          onPointerDown={(e) => e.stopPropagation()}
          className="ms-auto ps-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover/widget:opacity-100"
        >
          <RemoveHeaderButton drag={drag} />
        </span>
      </div>
      {/* Contain a runtime throw INSIDE a known kind's body to this one card —
          the shared WidgetLayer mounts one instance for the whole board, so an
          uncaught throw would blank it. resetKeys uses `rawParams` (the raw
          Y.Map value), NOT the freshly-parsed `data` object (a new ref every
          render → would re-arm every render and loop a genuinely-broken widget)
          and NOT `JSON.stringify(data)` (O(size) per render — image cards can
          hold a ~700 KB data URI, so serializing on every select/hover/drag is
          real jank). `rawParams` is render-stable (see the observe effect's
          `[rawParams]` dep). Under PB-418 char-merge its reference changes only
          when the params KEY is replaced (first materialization, or a remote
          full-replace) — NOT on an in-place `Y.Text`/scalar edit, which mutates
          the same Y.Map. Consequence: a crashed char-merge card re-arms on
          remount (reload / structural change), not on an in-place edit. Accepted
          (PB-419): `paramsFromY` coerces malformed params to defaults, so a body
          throw is a code path, not bad data an edit would fix. Deliberately NOT
          adding the observeDeep render tick to resetKeys — that would re-arm
          every keystroke and loop a genuinely-broken widget (the case above).
          The header (drag + remove) stays OUTSIDE the boundary so a broken card
          is still movable/removable. */}
      <div
        className="min-h-0 flex-1"
        onBlurCapture={(event) => {
          if (
            event.relatedTarget instanceof Node &&
            event.currentTarget.contains(event.relatedTarget)
          )
            return;
          onCanvasMutation?.(
            {
              target: "widget",
              action: "finish",
              id: drag.widgetId,
              entry: ymap,
            },
            () => {},
          );
        }}
      >
        <WidgetErrorBoundary
          widgetName={def.label}
          resetKeys={[drag.widgetId, rawParams]}
        >
          <div
            ref={bodyRef}
            className="contents"
            onSelect={captureSelection}
            onFocusCapture={captureSelection}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={() => {
              composingRef.current = false;
              captureSelection();
            }}
          >
            <Component
              data={data}
              onChange={handleChange}
              readOnly={drag.readOnly}
            />
          </div>
        </WidgetErrorBoundary>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SQL (entity) storage path
// ---------------------------------------------------------------------------

function SqlWidgetBody({
  ymap,
  def,
  kind,
  drag,
  lockState,
  onAskAI,
  onEditingChange,
  solveId,
  hostMetadata,
}: CanvasWidgetProps & {
  readonly def: SqlWidgetDef<unknown>;
  readonly kind: WidgetKind;
  readonly drag: DragProps;
}): ReactElement {
  const entityId = ymap.get("widgetId");
  const resolver = useWidgetEntityResolver();

  if (typeof entityId !== "string" || entityId.length === 0) {
    return (
      <SqlChrome def={def} kind={kind} drag={drag} lockState={lockState}>
        <PlaceholderBody label="Widget reference missing — try removing this placement." />
      </SqlChrome>
    );
  }

  if (!resolver) {
    console.warn(
      `@playbookai/ui: widget '${kind}' is SQL-storage but no WidgetEntityResolverProvider is mounted`,
    );
    return (
      <SqlChrome def={def} kind={kind} drag={drag} lockState={lockState}>
        <PlaceholderBody label="Widget resolver not configured." />
      </SqlChrome>
    );
  }

  return (
    <SqlWidgetInner
      def={def}
      entityId={entityId}
      resolverHook={resolver.useWidget}
      readOnly={drag.readOnly}
      lockState={lockState}
      drag={drag}
      kind={kind}
      onAskAI={onAskAI}
      onEditingChange={onEditingChange}
      solveId={solveId ?? ""}
      hostMetadata={hostMetadata}
    />
  );
}

function SqlWidgetInner({
  def,
  entityId,
  resolverHook,
  readOnly,
  lockState,
  drag,
  kind,
  onAskAI,
  onEditingChange,
  solveId,
  hostMetadata,
}: {
  def: SqlWidgetDef<unknown>;
  entityId: string;
  resolverHook: <T>(id: string) => WidgetEntityHandle<T>;
  readOnly?: boolean | undefined;
  lockState?: CanvasWidgetLockState | null | undefined;
  drag: DragProps;
  kind: WidgetKind;
  onAskAI?: ((request: WidgetAskAIRequest) => void) | undefined;
  onEditingChange?: ((widgetId: string, editing: boolean) => void) | undefined;
  solveId: string;
  hostMetadata?: WidgetHostMetadata | undefined;
}): ReactElement {
  const handle = resolverHook<unknown>(entityId);
  /* PB-4923 AC5. Lifted off the handle here so every control below — card
     body, expanded editor, xyflow node, table cell — reaches it with one hook
     and no prop drilling.

     Read-only viewers claim nothing: there is no draft to protect, and an
     editing session that nobody can act on would defer AI edits for its whole
     TTL over a field being looked at.

     The value is memoised on a BOOLEAN, and the callback is reached through a
     ref, for two reasons that turned out to be the same one. `beginFieldEdit`
     is a plain function declared in the resolver's body, so it is a new
     identity on every render — the previous `[beginFieldEdit, readOnly]` memo
     therefore never hit, and the provider handed every control a fresh object
     each time regardless of what the comment claimed. Keying on whether
     leasing is possible at all makes the value genuinely stable, so a control's
     context subscription only fires when that answer changes. It also lets the
     React Compiler preserve the memoization: it treats the handle returned by
     an opaque prop call as mutable, so a function read off it can never be a
     dependency it is able to reproduce.

     The ref is SEEDED by `useRef` and refreshed in an effect, the same shape
     `useRemoteFilterOptions` already uses here. Seeding is
     what makes the mount case safe: a control that focuses during layout —
     `funnel`'s item editor calls `.focus()` in a `useLayoutEffect`, which runs
     before any passive effect — reads the value the first render already put
     there, so its claim is never dropped. */
  const beginFieldEdit = handle.beginFieldEdit;
  const beginFieldEditRef = useRef(beginFieldEdit);
  useEffect(() => {
    beginFieldEditRef.current = beginFieldEdit;
  }, [beginFieldEdit]);
  const canLeaseFields = beginFieldEdit !== undefined && readOnly !== true;
  const fieldEditSession = useMemo<WidgetFieldEditSession | null>(
    () =>
      canLeaseFields
        ? {
            begin: (targetKey: string) => beginFieldEditRef.current!(targetKey),
          }
        : null,
    [canLeaseFields],
  );
  const [expanded, setExpanded] = useState(false);
  const [headerActionOpen, setHeaderActionOpen] = useState(false);
  const expandedRef = useRef(expanded);
  const onEditingChangeRef = useRef(onEditingChange);

  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);

  useEffect(() => {
    onEditingChangeRef.current = onEditingChange;
  }, [onEditingChange]);

  useEffect(() => {
    return (): void => {
      if (expandedRef.current) {
        onEditingChangeRef.current?.(drag.widgetId, false);
      }
    };
  }, [drag.widgetId]);

  if (handle.error) {
    return (
      <SqlChrome def={def} kind={kind} drag={drag} lockState={lockState}>
        <PlaceholderBody
          label={`Could not load widget (${handle.error.message ?? "unknown error"}).`}
        />
      </SqlChrome>
    );
  }
  if (handle.isLoading || handle.data === undefined) {
    return (
      <SqlChrome def={def} kind={kind} drag={drag} lockState={lockState}>
        <PlaceholderBody label="Loading…" muted />
      </SqlChrome>
    );
  }
  if (
    handle.schemaVersion !== undefined &&
    handle.schemaVersion !== WIDGET_CURRENT_SCHEMA_VERSIONS[kind]
  ) {
    return (
      <SqlChrome def={def} kind={kind} drag={drag} lockState={lockState}>
        <PlaceholderBody label="Widget cannot be loaded because its saved version is no longer supported." />
      </SqlChrome>
    );
  }

  const schema = def.schema;
  let data: unknown;
  try {
    data = schema.parse(handle.data);
  } catch (err) {
    console.warn(
      `@playbookai/ui: '${def.kind}' params failed schema validation`,
      err,
    );
    return (
      <SqlChrome def={def} kind={kind} drag={drag} lockState={lockState}>
        <PlaceholderBody
          label={
            kind === "problemTree"
              ? "Widget cannot be loaded because its saved data is no longer supported."
              : "Widget data shape did not validate. Refresh, or contact support if persistent."
          }
        />
      </SqlChrome>
    );
  }

  /**
   * PB-4923 deliberately does NOT change this seam.
   *
   * An editor keeps emitting complete params and this keeps handing them to
   * `patch`. Whether that becomes a bounded set of operations or a whole-params
   * write is the HOST's decision, taken at send time against the last
   * server-acknowledged value — see `WidgetEntityResolver` in
   * `packages/solve-feature`. Deciding it here would be wrong twice over: this
   * component sees one keystroke at a time, so it could not describe a whole
   * debounced edit; and `@playbookai/ui` is data-blind, so the transport is not
   * its call.
   */
  const handleChange = (
    next: unknown,
    options?: WidgetChangeOptions,
  ): Promise<void> | undefined => {
    const parsed = schema.safeParse(next);
    if (!parsed.success) {
      console.warn(
        `@playbookai/ui: refused to send invalid params for '${def.kind}'`,
        parsed.error,
      );
      return undefined;
    }
    // A person pick's save is awaited by its field (d25); everything else is
    // fire-and-forget, as before.
    const saved = handle.patch(parsed.data, options);
    return options?.grantViewerIfMissing ? Promise.resolve(saved) : undefined;
  };
  const Component = def.Component as React.ComponentType<{
    data: unknown;
    onChange: (
      next: unknown,
      options?: WidgetChangeOptions,
    ) => Promise<void> | undefined;
    readOnly?: boolean | undefined;
    hostMetadata?: WidgetHostMetadata | undefined;
    headerActionOpen?: boolean | undefined;
    onHeaderActionOpenChange?: ((open: boolean) => void) | undefined;
  }>;
  const footer = def.renderFooter
    ? def.renderFooter(data, {
        solveId,
        ...(hostMetadata ? { hostMetadata } : {}),
      })
    : undefined;
  const title = def.renderTitle ? def.renderTitle(data) : def.label;
  const ExpandedComponent = def.ExpandedComponent as
    | React.ComponentType<{
        data: unknown;
        onChange: (
          next: unknown,
          options?: WidgetChangeOptions,
        ) => Promise<void> | undefined;
        readOnly?: boolean | undefined;
      }>
    | undefined;
  const expandable =
    def.expandable === true ||
    ExpandedComponent !== undefined ||
    hasExpandedWidget(kind);
  const askAIRequest: WidgetAskAIRequest = {
    canvasWidgetId: drag.widgetId,
    entityId,
    kind,
    label: def.label,
    ...(solveId ? { solveId } : {}),
    ...(def.askAiPrompt ? { prompt: def.askAiPrompt } : {}),
  };

  return (
    <WidgetFieldEditSessionProvider value={fieldEditSession}>
      <div className="group/widget relative h-full w-full flex flex-col">
        <div className="flex-1 min-h-0">
          <ModuleCard
            title={title}
            headerLeadingSlot={<HeaderDragGrip drag={drag} />}
            subtitle={
              def.renderSubtitle ? def.renderSubtitle(data) : def.subtitle
            }
            accent={def.accent as ModuleCardAccent}
            icon={<def.icon className="w-3.5 h-3.5" />}
            selected={drag.selected}
            dimmed={lockState?.dimmed === true}
            onSelect={() => drag.onSelect?.(drag.widgetId)}
            onAskAI={onAskAI ? () => onAskAI(askAIRequest) : undefined}
            onExpand={
              expandable
                ? () => {
                    setExpanded(true);
                    onEditingChange?.(drag.widgetId, true);
                  }
                : undefined
            }
            headerExtras={
              <>
                {def.headerAffordance ? (
                  <HeaderAffordancePill
                    label={def.headerAffordance.label}
                    accent={def.accent as ModuleCardAccent}
                    pressed={
                      def.headerAffordance.action === "body"
                        ? headerActionOpen
                        : undefined
                    }
                    onClick={
                      readOnly ||
                      (def.headerAffordance.action !== "body" && !expandable)
                        ? undefined
                        : () => {
                            if (def.headerAffordance?.action === "body") {
                              setHeaderActionOpen((open) => !open);
                            } else {
                              setExpanded(true);
                              onEditingChange?.(drag.widgetId, true);
                            }
                          }
                    }
                  />
                ) : null}
                {lockState ? <WidgetLockPill label={lockState.label} /> : null}
                <RemoveHeaderButton drag={drag} />
              </>
            }
            footer={
              footer ? (
                <>
                  <span>{footer.left}</span>
                  <span>{footer.right}</span>
                </>
              ) : undefined
            }
          >
            <Component
              data={data}
              onChange={handleChange}
              readOnly={readOnly}
              hostMetadata={hostMetadata}
              headerActionOpen={headerActionOpen}
              onHeaderActionOpenChange={setHeaderActionOpen}
            />
          </ModuleCard>
        </div>
        {expandable ? (
          <ExpandedCardModal
            open={expanded}
            onClose={() => {
              setExpanded(false);
              onEditingChange?.(drag.widgetId, false);
            }}
            title={title}
          >
            {ExpandedComponent ? (
              <ExpandedComponent
                data={data}
                onChange={handleChange}
                readOnly={readOnly}
              />
            ) : (
              <ExpandedWidgetEditor
                kind={kind}
                data={data}
                onChange={handleChange}
                readOnly={readOnly}
              />
            )}
          </ExpandedCardModal>
        ) : null}
      </div>
    </WidgetFieldEditSessionProvider>
  );
}

function SqlChrome({
  def,
  drag,
  lockState,
  children,
}: {
  def: SqlWidgetDef<unknown>;
  kind: WidgetKind;
  drag: DragProps;
  lockState?: CanvasWidgetLockState | null | undefined;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div className="group/widget relative h-full w-full flex flex-col">
      <div className="flex-1 min-h-0">
        <ModuleCard
          title={def.label}
          headerLeadingSlot={<HeaderDragGrip drag={drag} />}
          subtitle={def.subtitle}
          accent={def.accent as ModuleCardAccent}
          icon={<def.icon className="w-3.5 h-3.5" />}
          selected={drag.selected}
          dimmed={lockState?.dimmed === true}
          onSelect={() => drag.onSelect?.(drag.widgetId)}
          headerExtras={
            <>
              {def.headerAffordance ? (
                <HeaderAffordancePill
                  label={def.headerAffordance.label}
                  accent={def.accent as ModuleCardAccent}
                />
              ) : null}
              {lockState ? <WidgetLockPill label={lockState.label} /> : null}
              <RemoveHeaderButton drag={drag} />
            </>
          }
        >
          {children}
        </ModuleCard>
      </div>
    </div>
  );
}

function WidgetLockPill({ label }: { label: string }): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-dense-1 font-medium border bg-pb-amber/10 text-pb-amber-text border-pb-amber/30">
      <Lock className="w-3 h-3" aria-hidden />
      {label}
    </span>
  );
}

/**
 * The header trash button, and the confirmation it opens.
 *
 * Both the SQL and the Yjs header render this, so every canvas widget removal
 * a person can initiate — SOLVE, VSM and Hoshin alike — passes through the
 * same dialog. The host's `onRemove` fires exactly once, from the dialog's
 * confirm; every dismissal leaves the widget and its placement untouched.
 * Read-only and protected widgets (the default roster) never render the
 * button, so they never reach the dialog either.
 */
function RemoveHeaderButton({
  drag,
}: {
  drag: DragProps;
}): ReactElement | null {
  const [confirming, setConfirming] = useState(false);
  if (drag.readOnly || !drag.onRemove) return null;
  return (
    <>
      <Tooltip content={`Remove ${drag.label}`} describeChild={false}>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          aria-label={`Remove ${drag.label}`}
          className="p-1.5 rounded-md text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </Tooltip>
      {/* Portaled, but React events still bubble along THIS tree — which is
          why the dialog sits inside the header's own stopPropagation wrappers
          (the SQL card's header-actions row, the Yjs header's remove span):
          a press inside the dialog can neither select the card nor begin a
          header drag. */}
      <CanvasWidgetRemoveDialog
        open={confirming}
        label={drag.label}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          drag.onRemove?.(drag.widgetId);
        }}
      />
    </>
  );
}

function PlaceholderBody({
  label,
  muted = false,
}: {
  label: string;
  muted?: boolean;
}): ReactElement {
  return (
    <div
      className={cn(
        "flex-1 grid place-items-center text-center text-xs italic px-3",
        muted ? "text-pb-text-muted/70" : "text-pb-text-muted",
      )}
    >
      {label}
    </div>
  );
}

export type { WidgetDef };
